import React, { useEffect, useState } from 'react';
import CustomInput from '../../components/form/CustomInput';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from "yup";
import { useFormik } from "formik";
import { resetConfirm, validaToken } from '../../helpers/configRoutes/backend_helper';
import CustomButton from '../../components/form/CustomButton';

const AsignPassword = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { token } = useParams();

    const validation = useFormik({
        initialValues: {
            email: '',
            password: '',
            passwordConfirm: '',
            tokenSms: '',
            token: token
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Correo electrónico no válido")
                .required("Ingresa tu correo electrónico"),
            password: Yup.string()
                .required("Ingresa tu contraseña")
                .min(8, "La contraseña debe tener al menos 8 caracteres")
                .matches(/[A-Z]/, "La contraseña debe contener al menos una letra mayúscula")
                .matches(/[a-z]/, "La contraseña debe contener al menos una letra minúscula")
                .matches(/[0-9]/, "La contraseña debe contener al menos un número"),
            passwordConfirm: Yup.string()
                .oneOf([Yup.ref('password'), null], "Las contraseñas no coinciden")
                .required("Confirma tu contraseña"),
            tokenSms: Yup.string()
                .required("Ingresa el código de seguridad enviado por SMS"),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const response = await resetConfirm(values);
            if (response.status === true) {
                navigate('/login');
            } else {
                if (response.errors?.length) {
                    setErrors(response.errors);
                }
            }
            setLoading(false);
        }
    });

    useEffect(() => {
        const getValidaToken = async () => {
            const response = await validaToken(validation.values);
            if (response.status) {
                validation.setFieldValue('email', response.email);
            }
        };
        getValidaToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen w-full bg-cs-bg-alt flex items-center justify-center px-4">
            <div className="w-full max-w-5xl bg-cs-bg rounded-3xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2 border border-cs-border relative">
                {/* Left side - branding */}
                <div className="hidden md:flex flex-col justify-between bg-cs-primary-soft px-10 py-8 relative">
                    <div>
                        {/* Logo */}
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center gap-3">
                                <div className="flex w-140 h-140 items-center justify-center">
                                    <img src="/logoA.png" />
                                </div>
                            </div>
                        </div>

                        <h1 className="mt-1 text-3xl font-semibold text-cs-dark leading-snug">
                            Secure access for your CargoSmart account.
                        </h1>
                        <p className="mt-2 text-sm text-cs-muted">
                            Set your password and verify your identity to start managing your loads,
                            operators and AI operations with full security.
                        </p>
                    </div>

                    <div className="mt-10 space-y-2 text-xs text-cs-muted">
                        <p>• Token validation linked to your account</p>
                        <p>• Strong password requirements for better protection</p>
                        <p>• Encrypted access managed by CargoSmart</p>
                    </div>

                    <div className="pointer-events-none absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_top,_#FF7A00_0,_transparent_55%)]" />
                </div>

                {/* Right side - form */}
                <div className="flex flex-col justify-center px-8 py-10">
                    {/* Header */}
                    <div className="mb-6 text-center md:text-left">
                        <p className="text-xs uppercase tracking-[0.18em] text-cs-muted mb-2">
                            Account verification
                        </p>
                        <h2 className="text-2xl font-semibold text-cs-dark">
                            Set your new password
                        </h2>
                        <p className="text-sm text-cs-muted mt-2">
                            Please confirm your email, create a secure password and enter
                            the SMS security code we sent you.
                        </p>
                    </div>

                    <form
                        className="space-y-4"
                        onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                            return false;
                        }}
                    >
                        {/* Email */}
                        <div>
                            <CustomInput
                                label="Email address"
                                type="email"
                                name="email"
                                labelClassName="text-sm font-medium text-cs-dark mb-1"
                                error={validation.errors.email}
                                value={validation.values.email}
                                handleChange={validation.handleChange}
                                className="w-full"
                                disabled // viene del token, mejor solo lectura
                            />
                        </div>

                        {/* Passwords */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <CustomInput
                                label="New password"
                                type="password"
                                name="password"
                                labelClassName="text-sm font-medium text-cs-dark mb-1"
                                error={validation.errors.password}
                                value={validation.values.password}
                                handleChange={validation.handleChange}
                                className="w-full"
                            />
                            <CustomInput
                                label="Confirm password"
                                type="password"
                                name="passwordConfirm"
                                labelClassName="text-sm font-medium text-cs-dark mb-1"
                                error={validation.errors.passwordConfirm}
                                value={validation.values.passwordConfirm}
                                handleChange={validation.handleChange}
                                className="w-full"
                            />
                        </div>

                        {/* SMS token */}
                        <div>
                            <CustomInput
                                label="Security code (SMS)"
                                type="text"
                                name="tokenSms"
                                labelClassName="text-sm font-medium text-cs-dark mb-1"
                                error={validation.errors.tokenSms}
                                value={validation.values.tokenSms}
                                handleChange={validation.handleChange}
                                className="w-full"
                                placeholder="Enter the code sent to your phone"
                            />
                        </div>

                        {/* Submit */}
                        <div className="pt-4">
                            <CustomButton
                                label={"Confirm"}
                                loading={loading}
                                textLoad="Processing..."
                                type="success"
                                className="w-full bg-cs-primary hover:bg-cs-primary-hover  py-3 rounded-2xl transition-all shadow-md hover:shadow-lg"
                                text="Confirm and access"
                            />
                        </div>

                        {/* Back to login */}
                        <p className="pt-3 text-center text-xs text-cs-muted">
                            Already have your password?
                            <span
                                onClick={() => navigate('/login')}
                                className="ml-1 text-cs-primary hover:text-cs-primary-hover cursor-pointer font-medium"
                            >
                                Go back to login
                            </span>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AsignPassword;
